import { useState, useEffect } from "react";
import Toast from "../../global/toastmessage";
import api from "../../api/api";

const UserModal = ({ onClose, onSave, editData }) => {
  const [form, setForm] = useState({
    company_name: "",
    name: "",
    email: "",
    mobile_number: "",
    status: "A",
  });

  const [initialForm, setInitialForm] = useState({});
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "error"
  });

  const [showOtpBox, setShowOtpBox] = useState(false);
  const [otp, setOtp] = useState("");
  const [serverOtp, setServerOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);

  // Loading states
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  useEffect(() => {
    if (editData) {
      setForm(editData);
      setInitialForm(editData);
    }
  }, [editData]);

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  // -----------------------------------
  // Send OTP to EXISTING number
  // -----------------------------------
  const sendOtp = async () => {
    setSendingOtp(true);

    try {
      const response = await api.post("/v1/admin-user/send-otp", {
        phone_number: initialForm.mobile_number,   // send OTP to OLD number
      });

      if (response.status === 200) {
        setServerOtp(response?.Otp);

        setToast({
          open: true,
          message: "OTP sent to existing mobile number",
          severity: "success",
        });
      } else {
        setToast({
          open: true,
          message: response.data.message,
          severity: "error",
        });
      }
    } catch (error) {
      console.log(error);
      setToast({
        open: true,
        message: "Failed to send OTP",
        severity: "error",
      });
    }

    setSendingOtp(false);
  };

  // -----------------------------------
  // Input Change
  // -----------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile_number" && value !== "" && !/^\d+$/.test(value)) return;

    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: false });

    // If mobile is changed → require OTP verification
    if (editData && name === "mobile_number") {
      if (value !== initialForm.mobile_number) {
        setShowOtpBox(true);
        setOtpVerified(false);
      } else {
        setShowOtpBox(false);
        setOtpVerified(true);
      }
    }
  };

  // -----------------------------------
  // Validate
  // -----------------------------------
  const validate = () => {
    let newErrors = {};

    if (!form.company_name.trim()) newErrors.company_name = true;
    if (!form.name.trim()) newErrors.name = true;
    if (!form.email.trim()) newErrors.email = true;
    if (!form.mobile_number.trim()) newErrors.mobile_number = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setToast({
        open: true,
        message: "Please fill all mandatory fields.",
        severity: "error",
      });
      return false;
    }

    // If mobile changed then OTP must be verified
    if (editData && showOtpBox && !otpVerified) {
      setToast({
        open: true,
        message: "Please verify OTP before saving.",
        severity: "error",
      });
      return false;
    }

    return true;
  };

  const getChangedFields = () => {
    const changed = {};
    Object.keys(form).forEach((key) => {
      if (form[key] !== initialForm[key]) changed[key] = form[key];
    });
    return changed;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = editData ? getChangedFields() : form;
    onSave(payload, editData);
  };

  // -----------------------------------
  // Verify OTP
  // -----------------------------------
  const verifyOtp = () => {
    setVerifyingOtp(true);

    setTimeout(() => {
      if (otp === serverOtp) {
        setOtpVerified(true);
        setToast({
          open: true,
          message: "OTP verified successfully",
          severity: "success",
        });
      } else {
        setOtpVerified(false);
        setToast({
          open: true,
          message: "Invalid OTP",
          severity: "error",
        });
      }

      setVerifyingOtp(false);
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl mx-auto my-8 p-6 relative">

        <h3 className="text-lg font-semibold mb-4">
          {editData ? "Edit User" : "Add User"}
        </h3>

        <div className="space-y-3">

          <input
            type="text"
            name="company_name"
            placeholder="Company Name"
            value={form.company_name}
            onChange={handleChange}
            className={`w-full border p-2 rounded ${errors.company_name ? "border-red-600" : "border-gray-300"}`}
          />

          <input
            type="text"
            name="name"
            placeholder="Person Name"
            value={form.name}
            onChange={handleChange}
            className={`w-full border p-2 rounded ${errors.name ? "border-red-600" : "border-gray-300"}`}
          />

          <input
            type="email"
            name="email"
            disabled={!!editData}
            placeholder="Email ID"
            value={form.email}
            onChange={handleChange}
            className={`w-full border p-2 rounded ${errors.email ? "border-red-600" : "border-gray-300"}`}
          />

          <input
            type="text"
            name="mobile_number"
            placeholder="Mobile Number"
            maxLength={10}
            value={form.mobile_number}
            onChange={handleChange}
            className={`w-full border p-2 rounded ${errors.mobile_number ? "border-red-600" : "border-gray-300"}`}
          />

          {/* OTP Section */}
   {editData && showOtpBox && (
  <div className="space-y-2">

    <div className="w-full flex gap-3">

      <input
        type="text"
        maxLength={4}
        placeholder="Enter OTP"
        value={otp}
        onChange={(e) => setOtp(e.target.value)}
        className="border p-2 rounded w-full"
      />

      {/* ONE BUTTON — handles BOTH send + verify */}
      <button
        onClick={() => {
          if (!serverOtp) {
            sendOtp();          // First click → send OTP
          } else {
            verifyOtp();        // After OTP received → verify
          }
        }}
        disabled={sendingOtp || verifyingOtp}
        className="px-4 py-1 bg-[#145388] w-full text-white rounded hover:bg-blue-500"
      >
        {sendingOtp
          ? "Sending..."
          : verifyingOtp
          ? "Verifying..."
          : !serverOtp
          ? "Send OTP"
          : "Verify OTP"}
      </button>

    </div>

    {otpVerified && (
      <p className="text-green-700 text-sm font-medium">OTP Verified ✓</p>
    )}

  </div>
)}


          {editData && (
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="border w-full p-2 rounded"
            >
              <option value="A">Active</option>
              <option value="I">Inactive</option>
            </select>
          )}

        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-1 bg-gray-300 rounded hover:bg-gray-400"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={editData && showOtpBox && !otpVerified}
            className={`px-4 py-1 rounded text-white 
              ${editData && showOtpBox && !otpVerified ? "bg-gray-400 cursor-not-allowed" : "bg-red-800 hover:bg-red-700"}`}
          >
            Save
          </button>
        </div>

      </div>

      <Toast
        open={toast.open}
        onClose={handleCloseToast}
        severity={toast.severity}
        message={toast.message}
      />

    </div>
  );
};

export default UserModal;
